import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { bookingShape, locationShape, userShape } from 'constants/propTypes';
import { pathOr } from 'ramda';
import moment from 'moment';

import { isSegmentInbound, nonCancelledEurostarAndGreenspeedLegs } from 'utils/booking';

import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';

import SeatSelection from 'components/shared/SeatSelection';
import { Page, ResponsiveColumn } from 'components/shared/Layout';
import MealChangeWarning from 'components/shared/MealChangeWarning';
import useUpdateMealsMutation from 'components/mutations/useUpdateMealsMutation';
import { buildMeal, getJourney, mergeMeals } from './AmendBooking.utils';
import AmendBookingCta from './AmendBookingCta';
import { RowContainer, ColumnContainer } from './AmendBooking.styles';
import SegmentContainer from './SegmentContainer';
import { isEurostarOrganization } from 'utils/user';

const AmendBooking = ({
  match: {
    params: { reference, id: segmentId }
  },
  history: { push },
  booking,
  location,
  user
}) => {
  const [selectedMeals, setSelectedMeals] = useState({});

  const selectMeal = (e, passengerFirstName, passengerLastName, id) => {
    const mealCode = e.target.value;
    setSelectedMeals({
      ...selectedMeals,
      [id]: buildMeal({ passengerFirstName, passengerLastName, mealCode, id })
    });
  };

  const redirectUrl = pathOr(`/manage/bookings/${reference}/journeys`, ['state', 'from'], location);

  const journey = getJourney(booking, segmentId);

  const {
    timing: { isoDate }
  } = journey;

  const redirect = () => push(redirectUrl);

  const isNewMealsSelection = !!journey.meals.find((meal) => {
    const selectedMeal = selectedMeals[meal.passenger.id];

    return selectedMeal && selectedMeal.mealCode !== meal.code;
  });

  const displayMealWarning = (outboundDateTime, mealsChanged) =>
    moment(outboundDateTime).subtract(2, 'days').isBefore(moment()) && Object.keys(mealsChanged).length > 0;

  const isInbound = isSegmentInbound(journey);

  const { reference: bookingReference, inbound: inboundSegments, outbound: outboundSegments, passengers } = booking;
  const meals = mergeMeals(isInbound ? inboundSegments : outboundSegments, selectedMeals, passengers);

  const [updateMeals, { error: updateMealsError, loading: isUpdatingMeals }] = useUpdateMealsMutation({
    bookingReference,
    inbound: isInbound ? meals : [],
    outbound: !isInbound ? meals : [],
    applySalesRestrictionOverrides: isEurostarOrganization(user)
  });

  if (updateMealsError) {
    throw new AgentPortalError(updateMealsError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: redirectUrl
    });
  }

  return (
    <Page>
      <SeatSelection
        visibleLegIds={[segmentId]}
        bookingData={booking}
        legId={segmentId}
        outbound={nonCancelledEurostarAndGreenspeedLegs(booking.outbound)}
        inbound={nonCancelledEurostarAndGreenspeedLegs(booking.inbound)}
        user={user}
        isMYB
      >
        {({
          renderedSeatSelection,
          isUpdatingSeats,
          disabledContinue,
          selectedSeats,
          selectedPassenger,
          selectedPassengers,
          areNewSeatsSelected,
          actions: { updatePassengerSeats, onSelectNewPassenger, onRemovePassengerSelection }
        }) => (
          <>
            <RowContainer>
              <AmendBookingCta
                {...{
                  updateMeals,
                  isUpdatingMeals,
                  redirect,
                  isNewMealsSelection,
                  areNewSeatsSelected,
                  updatePassengerSeats,
                  isUpdatingSeats,
                  disabledContinue
                }}
              />
            </RowContainer>
            <ResponsiveColumn smallScreen={16} bigScreens={12}>
              {renderedSeatSelection}
            </ResponsiveColumn>
            <RowContainer>
              <ColumnContainer>
                <SegmentContainer
                  id="segment_container"
                  segmentId={segmentId}
                  journey={journey}
                  currency={booking.currency}
                  bookingReference={reference}
                  selectMeal={selectMeal}
                  seats={selectedSeats}
                  selectedPassenger={selectedPassenger}
                  selectedPassengers={selectedPassengers}
                  onSelectNewPassenger={onSelectNewPassenger}
                  onRemovePassengerSelection={onRemovePassengerSelection}
                />
              </ColumnContainer>
            </RowContainer>
            {displayMealWarning(isoDate, selectedMeals) && <MealChangeWarning />}
            <RowContainer>
              <AmendBookingCta
                {...{
                  updateMeals,
                  isUpdatingMeals,
                  redirect,
                  isNewMealsSelection,
                  areNewSeatsSelected,
                  updatePassengerSeats,
                  isUpdatingSeats,
                  disabledContinue
                }}
              />
            </RowContainer>
          </>
        )}
      </SeatSelection>
    </Page>
  );
};

AmendBooking.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      reference: PropTypes.string,
      id: PropTypes.string
    })
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func
  }),
  booking: bookingShape,
  location: locationShape,
  user: userShape
};

export default AmendBooking;
